import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";
import TakeWebCamPicture from "./TakeWebCamPicture";
import { isValidEmail, isValidPhone, isValidName } from "../../Services/validation/validationUtiles";
import personIconPng from "../../assets/images/person_icon.png";
import passportIconPng from "../../assets/images/passport_icon.png";
import adminServices from "../../Services/adminService";
import visaService from "../../Services/visaService";
import "../../assets/css/groupForm.css";
import config from "../../config";
import { photoPopUpHtml } from "./formUtils";
import { isFileImage, isObjectNotEmpty, capitalizeFirstLetter } from "../../utils";

const formInputs = [
  { label: "שם המשרד המגיש/חברה", name: "companyName" },
  { label: "שם סוכן", name: "agentName" },
  { label: "אימייל (לא חובה)", name: "email" },
  { label: "טלפון", name: "phone" },
  { label: "כתובת בארץ", name: "addressInIsrael" },
  { label: "מקצוע", name: "profession" }
];

const defaultFormData = {
  companyName: "",
  agentName: "",
  email: "",
  phone: "",
  profession: "",
  addressInIsrael: "",
  pictureOfPerson: null,
  pictureOfPassport: null
};

function UnitedKingdomForm() {
  let { organizationName, destination, dateOfTrip, groupId, groupAgentName } = useParams();
  organizationName = decodeURIComponent(organizationName);
  destination = decodeURIComponent(destination);
  groupAgentName = decodeURIComponent(groupAgentName);

  const [formData, setFormData] = useState(defaultFormData);
  const [formatErrors, setFormatErrors] = useState({});
  const [takePictureType, setTakePictureType] = useState(null);
  const formQuestions = useRef();

  const fileInputs = [
    {
      label: "תמונת דרכון",
      icon: passportIconPng,
      name: "pictureOfPassport",
      ref: useRef()
    },
    {
      label: "תמונת פספורט",
      icon: personIconPng,
      name: "pictureOfPerson",
      ref: useRef()
    }
  ];

  const onChange = ({ target: { name, value } }) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [name]: value });
  };

  const onChangeFile = ({ target: { name, files } }) => {
    if (!isFileImage(files[0])) {
      Swal.fire("אופס,", "PDF ניתן להעלות רק תמונות או קבצי", "error");
      return;
    }
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[name];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [name]: files[0] });
  };

  const savePictureFromCamera = (takePictureType, imgSrc) => {
    setFormatErrors((prevFormatErrors) => {
      delete prevFormatErrors[takePictureType];
      return { ...prevFormatErrors };
    });
    setFormData({ ...formData, [takePictureType]: imgSrc });
  };

  const onFileInputClick = async (name, inputRef) => {
    const { isConfirmed } = await Swal.fire({
      html: photoPopUpHtml(name),
      confirmButtonText: "להמשיך",
      cancelButtonText: "ביטול",
      showCancelButton: true,
      showCloseButton: true
    });
    if (!isConfirmed) return;
    if (!config.isMobile) {
      const userResponse = await Swal.fire({
        icon: "question",
        text: "האם תרצ/י להתצטלם בעזרת המצלמה המחוברת למחשב/לפטופ או להעלות קובץ תמונה מהמחשב?",
        confirmButtonText: "לצלם תמונה",
        cancelButtonText: "להעלות תמונה",
        showCancelButton: true,
        showCloseButton: true
      });

      if (userResponse.value) {
        setTakePictureType(name);
      } else if (userResponse.dismiss === "cancel") {
        inputRef.current.click();
      }
    } else {
      inputRef.current.click();
    }
  };

  const validateForm = ({ companyName, agentName, email, phone, profession, addressInIsrael, pictureOfPerson, pictureOfPassport }) => {
    const formatErrors = {};

    if (!isValidName(companyName)) {
      formatErrors.companyName = "שם המשרד לא תקין";
    }

    if (!isValidName(agentName)) {
      formatErrors.agentName = "שם סוכן לא תקין";
    }

    if (email && !isValidEmail(email)) {
      formatErrors.email = "אימייל לא תקין";
    }

    if (!isValidPhone(phone)) {
      formatErrors.phone = "מספר טלפון לא תקין";
    }

    if (!isValidName(profession)) {
      formatErrors.profession = "מקצוע לא תקין";
    }

    if (!addressInIsrael || addressInIsrael.length < 3) {
      formatErrors.addressInIsrael = "כתובת לא תקינה";
    }

    if (!pictureOfPerson) {
      formatErrors.pictureOfPerson = true;
    }

    if (!pictureOfPassport) {
      formatErrors.pictureOfPassport = true;
    }

    setFormatErrors(formatErrors);
    return formatErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formatErrors = validateForm(formData);
    if (isObjectNotEmpty(formatErrors)) {
      formQuestions.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      return;
    }

    const form = new FormData();
    for (const file in formData) {
      form.append(file, formData[file]);
    }
    form.append("groupId", groupId);

    const addParticipantRes = await adminServices.addParticipantToGroup(form);
    const newGroupVisaRes = await visaService.createNewGroupVisa({
      visaData: {
        ...formData,
        destination: capitalizeFirstLetter(destination),
        pictureOfPassport: addParticipantRes.data.pictureOfPassportFileName,
        pictureOfPerson: addParticipantRes.data.pictureOfPersonFileName
      }
    });

    if (addParticipantRes?.success === true && newGroupVisaRes?.success === true) {
      setFormData(defaultFormData);
      Swal.fire("בוצע בהצלחה", "הטופס נשלח בהצלחה", "success");
    }
  };

  return (
    <div>
      <Header groupAgentName={groupAgentName} organizationName={organizationName} dateOfTrip={dateOfTrip} destination={destination} />
      <form className="group-form-form" onSubmit={handleSubmit} ref={formQuestions}>
        <h2 className="group-form-title">הכל כמעט מוכן לבואכם</h2>
        <div className="group-form-inputs-container">
          {formInputs.map(({ label, name }) => (
            <label key={name} className="group-form-input-label">
              {label}:
              <input className="group-form-input" value={formData[name]} name={name} type="text" placeholder={label} onChange={onChange} />
              {formatErrors[name] && <p className="text-format-error">* {formatErrors[name]}</p>}
            </label>
          ))}
        </div>

        <div className="group-form-inputs-container mb-20">
          {fileInputs.map(({ label, icon, name, ref }) => (
            <div key={name} className="group-form-file-input-container">
              <img src={icon} alt="icon" />

              <span className="group-form-file-input-text">
                <p className="group-form-file-input-text-label">{label}</p>
                {formData[name] ? (
                  <p className="group-form-file-input-text-success">מסמך צורף</p>
                ) : (
                  <p className={"group-form-file-input-text-falsy " + (formatErrors[name] ? "file-input-error" : "")}>שום מסמך לא צורף</p>
                )}
              </span>

              <button
                className="group-form-file-input-button"
                type="button"
                onClick={() => {
                  onFileInputClick(name, ref);
                }}
              >
                {formData[name] ? <>&#10004;</> : "צרף"}
              </button>

              <input hidden ref={ref} name={name} type="file" onChange={onChangeFile} />
            </div>
          ))}
        </div>
        <button className="submit-button" type="submit" value="Submit">
          שלח
        </button>
      </form>

      <TakeWebCamPicture
        takePictureType={takePictureType}
        setTakePictureType={setTakePictureType}
        savePictureFromCamera={savePictureFromCamera}
      />
    </div>
  );
}

export default UnitedKingdomForm;
